// Shared office UI for the portal document library (Session AFE): one DocumentManager
// drives BOTH the Company Documents page and the Site Documents card on the site detail
// page, parameterised by a small adapter (categories + the api namespace to call), so
// the two surfaces can never drift on validation, table shape or delete semantics.
//
// Upload is PDF-only, 20MB max (mirrors the server limit); an insurance category
// requires an expiry date (mirrors the server rule, which still enforces it). Category
// and file are immutable after upload; Remove is a SOFT delete behind the branded
// ConfirmDeleteModal archive variant (the file itself stays in Google Drive).
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { api } from "../../lib/api";
import type { CompanyDocCategoryKey, SiteDocCategoryKey } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";

export const MAX_DOCUMENT_MB = 20;

// Dropdown options. The labels MUST match the server's category label maps
// (server/src/modules/portalDocuments/portalDocuments.ts) so the option a user picks
// reads identically once it comes back as a row's categoryLabel.
export const COMPANY_DOC_CATEGORY_OPTIONS: (DocumentCategoryOption & { value: CompanyDocCategoryKey })[] = [
  { value: "HS_POLICY", label: "H&S Policy" },
  { value: "PUBLIC_LIABILITY", label: "Public Liability Insurance", insurance: true },
  { value: "EMPLOYERS_LIABILITY", label: "Employers Liability Insurance", insurance: true },
  { value: "RAMS", label: "RAMS" },
  { value: "COSHH", label: "COSHH Assessment" },
  { value: "SAFED_ANNEX", label: "Supplementary testing (SAFed guidance)" },
  { value: "OTHER", label: "Other document" },
];

export const SITE_DOC_CATEGORY_OPTIONS: (DocumentCategoryOption & { value: SiteDocCategoryKey })[] = [
  { value: "SITE_RAMS", label: "Site-specific RAMS" },
  { value: "SITE_RISK_ASSESSMENT", label: "Risk Assessment" },
  { value: "OTHER", label: "Other" },
];

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export interface DocumentCategoryOption {
  value: string;
  label: string;
  // Insurance categories require an expiry date (client mirror of the server rule).
  insurance?: boolean;
}

export interface ManagedDocument {
  id: string;
  category: string;
  categoryLabel: string;
  label: string;
  validUntil: string | null;
  validUntilDisplay: string | null;
  uploadedAtDisplay: string;
}

export interface DocumentManagerAdapter {
  categories: DocumentCategoryOption[];
  list: () => Promise<{ documents: ManagedDocument[] }>;
  upload: (file: File, fields: { category: string; label: string; validUntil?: string }) => Promise<unknown>;
  patch: (id: string, body: { label?: string; validUntil?: string | null }) => Promise<unknown>;
  remove: (id: string) => Promise<unknown>;
  downloadHref: (id: string) => string;
}

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

function categoryOf(options: DocumentCategoryOption[], value: string): DocumentCategoryOption | undefined {
  return options.find((o) => o.value === value);
}

function FormModal({
  title,
  onClose,
  onSubmit,
  busy,
  error,
  submitLabel,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  children: ReactNode;
}) {
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={onSubmit}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">{title}</h2>
        <div className="grid gap-3">{children}</div>
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Working..." : submitLabel}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function UploadDocumentModal({ adapter, onClose, onDone }: { adapter: DocumentManagerAdapter; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState(adapter.categories[0]!.value);
  const [label, setLabel] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const insurance = Boolean(categoryOf(adapter.categories, category)?.insurance);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return setError("A label is required.");
    if (insurance && !validUntil) return setError("An expiry date is required for insurance certificates.");
    if (!file) return setError("Choose a PDF to upload.");
    if (!isPdfFile(file)) return setError("Only PDF files can be uploaded here.");
    if (file.size > MAX_DOCUMENT_MB * 1024 * 1024) return setError(`That file is over ${MAX_DOCUMENT_MB}MB. Please upload a smaller one.`);
    setBusy(true);
    setError(null);
    try {
      await adapter.upload(file, { category, label: label.trim(), validUntil: validUntil || undefined });
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "Could not upload the document. Please try again."));
      setBusy(false);
    }
  }

  return (
    <FormModal title="Add a document" onClose={onClose} onSubmit={submit} busy={busy} error={error} submitLabel="Upload">
      <label>
        <span className={labelCls}>Category</span>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          {adapter.categories.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className={labelCls}>Label</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={200}
          placeholder="e.g. Health and Safety Policy 2026"
          className={inputCls}
        />
      </label>
      <label>
        <span className={labelCls}>Valid until{insurance ? " (required)" : " (optional)"}</span>
        <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>File (PDF, up to {MAX_DOCUMENT_MB}MB)</span>
        <input
          type="file"
          accept="application/pdf"
          aria-label="Document file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-ele-body file:mr-3 file:rounded-ele file:border-0 file:bg-ele-green file:px-4 file:py-2 file:font-menu file:text-xs file:uppercase file:tracking-wide file:text-ele-bg"
        />
        <p className="mt-1 text-xs text-ele-body opacity-70">
          The category and file cannot be changed later: remove the document and upload again to replace either.
        </p>
      </label>
    </FormModal>
  );
}

function EditDocumentModal({
  adapter,
  doc,
  onClose,
  onDone,
}: {
  adapter: DocumentManagerAdapter;
  doc: ManagedDocument;
  onClose: () => void;
  onDone: () => void;
}) {
  const [label, setLabel] = useState(doc.label);
  const [validUntil, setValidUntil] = useState(doc.validUntil ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const insurance = Boolean(categoryOf(adapter.categories, doc.category)?.insurance);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!label.trim()) return setError("A label is required.");
    if (insurance && !validUntil) return setError("An expiry date is required for insurance certificates.");
    setBusy(true);
    setError(null);
    try {
      await adapter.patch(doc.id, { label: label.trim(), validUntil: validUntil || null });
      onDone();
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the changes. Please try again."));
      setBusy(false);
    }
  }

  return (
    <FormModal title="Edit document" onClose={onClose} onSubmit={submit} busy={busy} error={error} submitLabel="Save">
      <p className="text-xs text-ele-body opacity-70">
        {doc.categoryLabel}. The category and file are fixed: remove and re-upload to change them.
      </p>
      <label>
        <span className={labelCls}>Label</span>
        <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} className={inputCls} />
      </label>
      <label>
        <span className={labelCls}>Valid until{insurance ? " (required)" : " (optional)"}</span>
        <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={inputCls} />
      </label>
    </FormModal>
  );
}

export function DocumentManager({ adapter, addLabel = "Add a document", emptyText }: { adapter: DocumentManagerAdapter; addLabel?: string; emptyText: string }) {
  const [rows, setRows] = useState<ManagedDocument[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ManagedDocument | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ManagedDocument | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  const reload = useCallback(() => {
    adapter
      .list()
      .then((r) => {
        setRows(r.documents);
        setLoaded(true);
        setLoadError(false);
      })
      .catch(() => {
        setLoaded(true);
        setLoadError(true);
      });
  }, [adapter]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await adapter.remove(removeTarget.id);
      setRemoveTarget(null);
      reload();
    } catch (err) {
      setRemoveError(friendlyApiError(err, "Could not remove the document. Please try again."));
    } finally {
      setRemoveBusy(false);
    }
  }

  const actionBtn =
    "max-sm:min-h-11 rounded-ele border px-2.5 py-1 font-menu text-xs tracking-wide transition";

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ele-body opacity-70">Shown to customers in the Client Portal.</p>
        <Button onClick={() => setAddOpen(true)} icon={<Plus aria-hidden size={15} />} className="shrink-0">
          {addLabel}
        </Button>
      </div>

      {!loaded ? (
        <Loader rows={3} />
      ) : loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          Could not load the documents. Refresh to try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-4 text-center text-sm text-ele-body">{emptyText}</p>
      ) : mobileCards ? (
        /* Session ALY mobile card list. Below md this table already keeps its whole Actions
           column visible (Download/Edit/Remove never carried max-md:hidden), but Label and
           Category still divide a fixed-layout row designed for four extra desktop-only
           columns, so a long label still clips. The card stacks instead: Label never truncates,
           Category comes along as a second line, and Valid until / Uploaded -- both desktop-only
           columns before this -- come back, matching the reclaim-on-cards pattern already used
           on the Invoices list. */
        <MobileCardList label="Documents">
          {rows.map((d) => (
            <MobileListCard
              key={d.id}
              primary={d.label}
              secondary={d.categoryLabel}
              meta={`Valid until ${d.validUntilDisplay ?? "None"}`}
              right={<span className="text-xs text-ele-body opacity-70">Uploaded {d.uploadedAtDisplay}</span>}
              actions={
                <>
                  <a
                    href={adapter.downloadHref(d.id)}
                    className="flex min-h-11 items-center rounded-ele border border-ele-blue px-3 font-menu text-xs tracking-wide text-ele-loler transition hover:bg-ele-blue/10"
                  >
                    Download
                  </a>
                  <button
                    type="button"
                    onClick={() => setEditTarget(d)}
                    className="flex min-h-11 items-center rounded-ele border border-ele-purple-accent px-3 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRemoveError(null);
                      setRemoveTarget(d);
                    }}
                    className="flex min-h-11 items-center rounded-ele border border-ele-danger/60 px-3 font-menu text-xs tracking-wide text-ele-danger transition hover:bg-ele-danger/10"
                  >
                    Remove
                  </button>
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "32%" }} />
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "13%" }} />
              <col className="max-md:hidden" style={{ width: "13%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Category</th>
                <th className="max-md:hidden px-3 py-2">Valid until</th>
                <th className="max-md:hidden px-3 py-2">Uploaded</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 text-ele-strong">{d.label}</td>
                  <td className="px-3 py-2 text-ele-body">{d.categoryLabel}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{d.validUntilDisplay ?? "None"}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{d.uploadedAtDisplay}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <a
                        href={adapter.downloadHref(d.id)}
                        className={`${actionBtn} border-ele-blue text-ele-loler hover:bg-ele-blue/10`}
                      >
                        Download
                      </a>
                      <button type="button" onClick={() => setEditTarget(d)} className={`${actionBtn} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveError(null);
                          setRemoveTarget(d);
                        }}
                        className={`${actionBtn} border-ele-danger/60 text-ele-danger hover:bg-ele-danger/10`}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <UploadDocumentModal
          adapter={adapter}
          onClose={() => setAddOpen(false)}
          onDone={() => {
            setAddOpen(false);
            reload();
          }}
        />
      )}
      {editTarget && (
        <EditDocumentModal
          adapter={adapter}
          doc={editTarget}
          onClose={() => setEditTarget(null)}
          onDone={() => {
            setEditTarget(null);
            reload();
          }}
        />
      )}
      <ConfirmDeleteModal
        open={removeTarget !== null}
        title="Remove document"
        message="Remove this document from the portal?"
        warning={
          removeTarget
            ? `"${removeTarget.label}" will no longer appear here or in the Client Portal. The file itself stays in Google Drive.`
            : null
        }
        confirmLabel="Remove"
        variant="archive"
        busy={removeBusy}
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={() => {
          if (!removeBusy) setRemoveTarget(null);
        }}
      />
    </div>
  );
}

// The Site Documents card on the site detail page (Details tab): the same manager,
// scoped to one site, in the DetailsTab card chrome.
export function SiteDocumentsCard({ siteId }: { siteId: string }) {
  const adapter = useMemo<DocumentManagerAdapter>(
    () => ({
      categories: SITE_DOC_CATEGORY_OPTIONS,
      list: () => api.siteDocuments.list(siteId),
      upload: (file, fields) => api.siteDocuments.upload(siteId, file, { ...fields, category: fields.category as SiteDocCategoryKey }),
      patch: (docId, body) => api.siteDocuments.patch(siteId, docId, body),
      remove: (docId) => api.siteDocuments.remove(siteId, docId),
      downloadHref: (docId) => api.siteDocuments.downloadHref(siteId, docId),
    }),
    [siteId],
  );
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Site documents</h2>
      <DocumentManager adapter={adapter} emptyText="No site documents yet. Add the site-specific RAMS or risk assessments here." />
    </div>
  );
}
